import { PauseCircle, PlayCircle } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { usePauseOnlineBooking } from "../hooks/use-pause-online-booking";
import { formatPauseEnd } from "../utils/pause-status";
import { PauseBookingDialog } from "./pause-booking-dialog";

/**
 * Settings card body for the online-booking pause. Lives outside the settings
 * form because pausing/resuming are their own mutations applied immediately.
 */
export function PauseBookingConfig() {
  const {
    loading,
    saving,
    isPaused,
    isIndefinite,
    pausedUntil,
    pausedMessage,
    salonTimezone,
    pause,
    resume,
  } = usePauseOnlineBooking();
  const { t, i18n } = useTranslation("settings");
  const [dialogOpen, setDialogOpen] = useState(false);

  if (loading) return <Skeleton className="h-16 w-full" />;

  const pauseEnd = formatPauseEnd(isIndefinite, pausedUntil, salonTimezone, i18n.language);

  return (
    <div className="space-y-4">
      {isPaused ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-amber-900 dark:border-amber-900/50 dark:bg-amber-900/20 dark:text-amber-200">
          <p className="text-sm font-medium">
            {pauseEnd
              ? t("pauseBookings.pausedUntil", { date: pauseEnd })
              : t("pauseBookings.pausedIndefinitely")}
          </p>
          {pausedMessage && <p className="mt-1 text-xs opacity-80">“{pausedMessage}”</p>}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">{t("pauseBookings.openNow")}</p>
      )}

      <div className="flex flex-col gap-2 sm:flex-row">
        {isPaused && (
          <Button onClick={resume} disabled={saving}>
            <PlayCircle className="w-4 h-4 mr-2" />
            {saving ? t("pauseBookings.resuming") : t("pauseBookings.resume")}
          </Button>
        )}
        <Button variant="outline" onClick={() => setDialogOpen(true)} disabled={saving}>
          <PauseCircle className="w-4 h-4 mr-2" />
          {isPaused ? t("pauseBookings.changePause") : t("pauseBookings.pause")}
        </Button>
      </div>

      <PauseBookingDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        saving={saving}
        onConfirm={pause}
        isPaused={isPaused}
        isIndefinite={isIndefinite}
        pausedUntil={pausedUntil}
        pausedMessage={pausedMessage}
        salonTimezone={salonTimezone}
        onResume={resume}
        openOnPauseForm
      />
    </div>
  );
}
